import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ControlContainer,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, debounceTime, filter } from 'rxjs';
import { take } from 'rxjs/operators';
import { CommissionPlanRuleService } from 'src/app/shared/store/commissions/commission-plan-rule/commission-plan-rule.service';
import { ContractService } from 'src/app/shared/store/contract/contract.service';
import { ContractModel } from 'src/app/shared/store/contract/types';
import { ProductCategoryService } from 'src/app/shared/store/product-category/product-category.service';
import { ProductCategoryModel } from 'src/app/shared/store/product-category/types';
import { Entity } from 'src/app/shared/store/view/types';
import { formatDate } from '../../../../../../shared/helper/datehelper';
import { CommissionPlanRuleModel } from '../../../../../../shared/store/commissions/commission-plan-rule/types';
import {
  DataType,
  DropdownStyle,
} from '../../../../../../shared/store/typeahead/types';
import { debugLog } from '../../../../../../shared/pipe/rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-contract-form-general-informations',
  templateUrl: './contract-form-general-informations.component.html',
  styleUrls: ['./contract-form-general-informations.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  standalone: false,
})
export class ContractFormGeneralInformationsComponent
  implements OnInit, OnDestroy
{
  form: FormGroup;
  createContractFormModel$: Observable<ContractModel>;
  contractDetail$: Observable<ContractModel>;
  commissionPlanRule$: Observable<CommissionPlanRuleModel>;
  productCategory$: Observable<ProductCategoryModel>;
  isContractReadOnly$: Observable<boolean>;
  commissionMaxValue: number = 100;

  isEditMode: boolean = false;
  isLoaded: boolean = false;

  protected readonly Entity = Entity;
  protected readonly DataType = DataType;
  protected readonly DropdownStyle = DropdownStyle;

  constructor(
    private parent: FormGroupDirective,
    private formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly contractService: ContractService,
    private readonly productCategoryService: ProductCategoryService,
    private readonly commissionPlanRuleService: CommissionPlanRuleService,
  ) {
    this.createContractFormModel$ =
      this.contractService.getContractModel.value$;
    this.contractDetail$ = this.contractService.getByUniqueId.value$;
    this.isContractReadOnly$ =
      this.contractService.getIfDataReadOnlyFromContractId.value$;
    this.commissionPlanRule$ =
      this.commissionPlanRuleService.getCurrentByProductLineId.value$;
    this.productCategory$ = this.productCategoryService.getById.value$;
  }

  ngOnDestroy(): void {
    this.commissionPlanRuleService.getCurrentByProductLineId.reset();
  }

  ngOnInit(): void {
    this.isEditMode = this.route.snapshot.params['id'];
    if (!this.isEditMode) {
      this.isLoaded = true;
    }

    this.form = this.parent.form;
    this.form.addControl(
      'generalInformationFormGroup',
      this.formBuilder.group({
        validationDate: [],
        approverId: [undefined],
        documentsReceivedDate: [],
        documentsSentDate: [],
        regularizationDate: [],
        monthlyFee: [undefined, [Validators.required, Validators.min(0.01)]],
        monthlyCost: [],
        taxRate: [],
        commissionRate: [
          undefined,
          [Validators.required, Validators.min(0.01)],
        ],
        applicationFees: [0],
        terminationDate: [],
        terminationId: [undefined], //FK new table "ContractTerminationReason" => Typeahead
        expiryMonth: [],
        expiryDay: [],
        annualFee: [0],
      }),
    );

    this.createContractFormModel$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x) => {
        this.form.patchValue({
          generalInformationFormGroup: {
            approverId: x.approverId,
            monthlyFee: x.monthlyFee === 0 ? undefined : x.monthlyFee,
            monthlyCost: x.monthlyCost,
            taxRate: x.taxRate,
            applicationFees: x.applicationFees,
            commissionRate: x.commissionRate,
          },
        });
      });

    this.contractDetail$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x) => {
        this.form.patchValue({
          generalInformationFormGroup: {
            validationDate: x.validationDate
              ? formatDate(x.validationDate, 'ToInverseSqlDate')
              : undefined,
            approverId: x.approverId,
            documentsReceivedDate: x.documentsReceivedDate
              ? formatDate(x.documentsReceivedDate, 'ToInverseSqlDate')
              : undefined,
            documentsSentDate: x.documentsSentDate
              ? formatDate(x.documentsSentDate, 'ToInverseSqlDate')
              : undefined,
            regularizationDate: x.regularizationDate
              ? formatDate(x.regularizationDate, 'ToInverseSqlDate')
              : undefined,
            monthlyFee: x.monthlyFee,
            monthlyCost: x.monthlyCost,
            taxRate: x.taxRate,
            commissionRate: x.commissionRate,
            applicationFees: x.applicationFees,
            terminationDate: x.terminationDate
              ? formatDate(x.terminationDate, 'ToInverseSqlDate')
              : undefined,
            terminationId: x.terminationReasonId,
            expiryMonth: x.expiryMonth,
            expiryDay: x.expiryDay,
            annualFee: x.annualFee,
          },
        });
        this.isLoaded = true;
      });

    this.commissionPlanRule$.pipe(untilDestroyed(this)).subscribe((x) => {
      if (x) {
        this.form.patchValue({
          generalInformationFormGroup: {
            commissionRate: x.value,
          },
        });
        this.commissionMaxValue = x.value;
      }
    });

    this.productCategory$.pipe(untilDestroyed(this)).subscribe((x) => {
      if (x) {
        this.form.patchValue({
          generalInformationFormGroup: {
            taxRate: x.defaultTaxRate,
          },
        });
      }
    });

    this.form.controls['generalInformationFormGroup']
      .get('commissionRate')
      ?.valueChanges.pipe(debounceTime(150))
      .subscribe((x) => {
        if (x > this.commissionMaxValue) {
          this.form.patchValue({
            generalInformationFormGroup: {
              commissionRate: this.commissionMaxValue,
            },
          });
        }
      });

    this.form.controls['generalInformationFormGroup']
      .get('monthlyCost')
      ?.valueChanges.pipe(debounceTime(150))
      .subscribe(() => {
        this.setAnnualFee();
      });

    this.form.controls['generalInformationFormGroup']
      .get('monthlyFee')
      ?.valueChanges.pipe(debounceTime(150))
      .subscribe(() => {
        this.setAnnualFee();
      });

    this.form.controls['productCategoryId'].valueChanges
      .pipe(
        filter((x) => this.form.dirty || this.isLoaded),
        untilDestroyed(this),
      )
      .subscribe((value) => {
        if (!Number.isNaN(+value) && +value > 0) {
          this.productCategoryService.getById.call(value);
        } else {
          this.form.patchValue({
            generalInformationFormGroup: {
              taxRate: 0,
            },
          });
        }
      });

    this.form.controls['productLineId'].valueChanges
      .pipe(
        filter((x) => this.form.dirty || this.isLoaded),
        untilDestroyed(this),
      )
      .subscribe((value) => {
        if (!Number.isNaN(+value) && +value > 0) {
          this.commissionPlanRuleService.getCurrentByProductLineId.call({
            id: value,
            chosenDate: new Date(),
            commissionType: 'CompanyCommission',
          });
        } else {
          this.commissionMaxValue = 100;
          this.form.patchValue({
            generalInformationFormGroup: {
              commissionRate: 0,
            },
          });
        }
      });
  }

  setAnnualFee(): void {
    let annualFee: number = 0;
    let monthlyFee: number;
    let monthlyCost: number;

    try {
      monthlyFee =
        +this.form.controls['generalInformationFormGroup'].get('monthlyFee')
          ?.value;
      monthlyCost =
        +this.form.controls['generalInformationFormGroup'].get('monthlyCost')
          ?.value;
    } catch (e) {
      monthlyFee = 0;
      monthlyCost = 0;
    }

    if (monthlyFee !== 0 || monthlyCost !== 0) {
      annualFee = (monthlyFee + monthlyCost) * 12;
    }

    this.form.controls['generalInformationFormGroup']
      .get('annualFee')
      ?.setValue(annualFee);
  }
}
